import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Settings, Save, Gem, Wrench, Zap } from "lucide-react";

const TIER_COLORS = { common: "#94A3B8", uncommon: "#10B981", rare: "#3B82F6", epic: "#B026FF", legendary: "#FFD600" };

export default function AdminGameSettings() {
  const [settings, setSettings] = useState({});
  const [resources, setResources] = useState([]);
  const [equipment, setEquipment] = useState({});
  const [workerTiers, setWorkerTiers] = useState([]);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    API.get("/admin/game-settings").then(r => {
      setSettings(r.data.settings || {});
      setResources(r.data.resources || []);
      setEquipment(r.data.equipment || {});
      setWorkerTiers(r.data.worker_tiers || []);
    }).catch(() => {});
  }, []);

  const saveMechanics = async () => {
    setLoading("mechanics");
    try {
      await API.put("/admin/game-settings", settings);
      toast.success("Game mechanics saved");
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const saveResource = async (name) => {
    setLoading(`res-${name}`);
    const res = resources.find(r => r.name === name);
    try {
      await API.put(`/admin/game-settings/resource/${name}`, { rarity: res.rarity, base_price: res.base_price, current_price: res.current_price, tier: res.tier });
      toast.success(`${name} updated`);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const updateResource = (name, field, value) => {
    setResources(prev => prev.map(r => r.name === name ? { ...r, [field]: value } : r));
  };

  const saveEquipment = async () => {
    setLoading("equipment");
    try {
      await API.put("/admin/game-settings/equipment", { equipment });
      toast.success("Equipment saved");
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const updateEquipItem = (slot, idx, field, value) => {
    setEquipment(prev => {
      const items = [...(prev[slot] || [])];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, [slot]: items };
    });
  };

  return (
    <AdminLayout title="Game Settings">
      <div className="space-y-6" data-testid="admin-game-settings">
        {/* Core Mechanics */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
          <div className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#00F0FF]" /><h3 className="text-lg font-bold text-[#F8FAFC]">Core Mechanics</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { key: "base_digs_per_day", label: "Base Digs/Day", def: 200 },
              { key: "max_idle_hours", label: "Max Idle Hours", def: 12 },
              { key: "food_cost_per_day", label: "Food Cost/Day", def: 10 },
              { key: "death_timer_days", label: "Death Timer (days)", def: 14 },
              { key: "death_warning_day", label: "Death Warning Day", def: 10 },
              { key: "energy_drain_per_day", label: "Energy Drain/Day", def: 10 },
              { key: "stamina_drain_per_day", label: "Stamina Drain/Day", def: 8 },
              { key: "efficiency_drain_per_day", label: "Efficiency Drain/Day", def: 5 },
              { key: "refill_cost_per_point", label: "Refill Cost/Point", def: 1 },
              { key: "starting_energy", label: "Starting Energy", def: 100 },
              { key: "starting_stamina", label: "Starting Stamina", def: 100 },
              { key: "max_energy", label: "Max Energy", def: 100 },
              { key: "referral_bonus_referrer", label: "Referral (Referrer)", def: 100 },
              { key: "referral_bonus_referred", label: "Referral (Referred)", def: 50 },
              { key: "starting_coins", label: "Starting Coins", def: 100 },
              { key: "starting_workers", label: "Starting Workers", def: 1 },
              { key: "starting_bonus_user_limit", label: "Bonus Limit (0=unlimited)", def: 0 },
            ].map(({ key, label, def }) => (
              <div key={key}>
                <Label className="text-[#94A3B8] text-[10px] uppercase tracking-wider">{label}</Label>
                <Input type="number" step="any" value={settings[key] ?? def} onChange={(e) => setSettings({ ...settings, [key]: parseFloat(e.target.value) })}
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono-game" data-testid={`setting-${key}`} />
              </div>
            ))}
            <div>
              <Label className="text-[#94A3B8] text-[10px] uppercase tracking-wider">Starting Worker Tier</Label>
              <select
                value={settings.starting_worker_tier_id ?? "bronze"}
                onChange={(e) => setSettings({ ...settings, starting_worker_tier_id: e.target.value })}
                className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-sm font-mono-game"
                data-testid="setting-starting_worker_tier_id">
                {workerTiers.length === 0 && <option value="bronze">Bronze (default)</option>}
                {workerTiers.map(t => (
                  <option key={t.tier_id} value={t.tier_id} disabled={t.enabled === false}>
                    {t.name} — {t.cost}c{t.enabled === false ? " (disabled)" : ""}
                  </option>
                ))}
              </select>
              <p className="text-[9px] text-[#64748B] mt-1">Free worker(s) given to new users at registration.</p>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-[10px] uppercase tracking-wider">Streak Rewards (7 days, comma-separated)</Label>
              <Input value={(settings.streak_rewards || [10,15,25,40,60,80,150]).join(",")}
                onChange={(e) => setSettings({ ...settings, streak_rewards: e.target.value.split(",").map(Number).filter(n => !isNaN(n)) })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono-game" data-testid="setting-streak" />
            </div>
          </div>
          <button onClick={saveMechanics} disabled={loading === "mechanics"} data-testid="save-mechanics-btn"
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {loading === "mechanics" ? "Saving..." : "Save Mechanics"}
          </button>
        </div>

        {/* Resource Rarities & Prices */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
          <div className="flex items-center gap-2"><Gem className="w-5 h-5 text-[#FFD600]" /><h3 className="text-lg font-bold text-[#F8FAFC]">Resource Rarities & Prices</h3></div>
          <p className="text-xs text-[#94A3B8]">Rarity values should sum to ~1.0. Higher rarity = rarer resource.</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2A3241]">
                  {["Resource", "Tier", "Rarity", "Base Price", "Current Price", ""].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-mono-game text-[#64748B] tracking-[0.15em] uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resources.map(r => (
                  <tr key={r.name} className="border-b border-[#2A3241]/30" data-testid={`resource-row-${r.name.toLowerCase()}`}>
                    <td className="px-3 py-2">
                      <span className="text-sm font-medium" style={{ color: TIER_COLORS[r.tier] || "#94A3B8" }}>{r.name}</span>
                    </td>
                    <td className="px-3 py-2">
                      <select value={r.tier} onChange={(e) => updateResource(r.name, "tier", e.target.value)}
                        className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                        {["common", "uncommon", "rare", "epic", "legendary"].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.01" value={r.rarity} onChange={(e) => updateResource(r.name, "rarity", parseFloat(e.target.value))}
                        className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 w-20 text-xs font-mono-game" />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.1" value={r.base_price} onChange={(e) => updateResource(r.name, "base_price", parseFloat(e.target.value))}
                        className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 w-20 text-xs font-mono-game" />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.1" value={r.current_price} onChange={(e) => updateResource(r.name, "current_price", parseFloat(e.target.value))}
                        className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-7 w-20 text-xs font-mono-game" />
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => saveResource(r.name)} disabled={loading === `res-${r.name}`} data-testid={`save-resource-${r.name.toLowerCase()}`}
                        className="px-2 py-1 text-[10px] font-bold uppercase bg-[#10B981]/20 text-[#10B981] rounded hover:bg-[#10B981]/30 disabled:opacity-50">
                        {loading === `res-${r.name}` ? "..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#64748B]">Current rarity sum: <span className="font-mono-game text-[#00F0FF]">{resources.reduce((s, r) => s + (r.rarity || 0), 0).toFixed(2)}</span> (should be ~1.0)</p>
        </div>

        {/* Equipment Settings */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
          <div className="flex items-center gap-2"><Wrench className="w-5 h-5 text-[#B026FF]" /><h3 className="text-lg font-bold text-[#F8FAFC]">Equipment Stats</h3></div>
          <p className="text-xs text-[#94A3B8]">Edit bonus multipliers and coin costs for each equipment item.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["tool", "shoes", "machine", "bag"].map(slot => (
              <div key={slot} className="bg-[#0B0E14] rounded-xl p-4 border border-[#2A3241]/30">
                <p className="text-xs font-mono-game text-[#FFD600] tracking-[0.15em] uppercase mb-3 capitalize">{slot}</p>
                <div className="space-y-2">
                  {(equipment[slot] || []).map((item, idx) => (
                    <div key={item.name} className="flex items-center gap-2" data-testid={`equip-setting-${slot}-${idx}`}>
                      <span className="text-xs text-[#94A3B8] w-28 truncate">{item.name}</span>
                      <div className="flex-1 flex gap-1">
                        <Input type="number" step="0.1" value={item.bonus} onChange={(e) => updateEquipItem(slot, idx, "bonus", parseFloat(e.target.value))}
                          className="bg-[#131722] border-[#2A3241] text-[#00F0FF] h-6 text-[10px] font-mono-game w-16" />
                        <span className="text-[10px] text-[#64748B] self-center">x</span>
                        <Input type="number" value={item.cost} onChange={(e) => updateEquipItem(slot, idx, "cost", parseInt(e.target.value))}
                          className="bg-[#131722] border-[#2A3241] text-[#FFD600] h-6 text-[10px] font-mono-game w-20" />
                        <span className="text-[10px] text-[#64748B] self-center">c</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={saveEquipment} disabled={loading === "equipment"} data-testid="save-equipment-btn"
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest bg-[#B026FF] text-white rounded-lg hover:bg-[#B026FF]/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {loading === "equipment" ? "Saving..." : "Save Equipment"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
